import React from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import {
  Grid,
  Text,
  Paper,
  Table,
  rem,
  Pagination,
  Group,
  Center,
  Button,
  Anchor,
  Checkbox,
  LoadingOverlay,
} from "@mantine/core";
import StyledPageTitle from "../components/StyledPageTitle";
import StyledSectionTitle from "../components/StyledSectionTitle";
import { useMantineTheme } from "@mantine/core";
import { BarChart } from "@mantine/charts";
import {
  JobFilter,
  useGetAccountSummaryQuery,
  useListJobsQuery,
} from "../graphql";
import DateDisplay from "../components/DateDisplay";
import useListVariables from "../hooks/useListVariables";
import TableHeaderCell from "../components/TableHeaderCell";
import { IconClipboardList, IconUsersGroup } from "@tabler/icons-react";
import FinishJobsButton from "../components/FinishJobsButton";
import ClockInOutButton from "../components/ClockInOutButton";
import RacketTypeBadge from "../components/RacketTypeBadge";
import { usePermissions } from "../contexts/PermissionsContext";
import { track } from "../utils/analytics";

const DashboardPage: React.FC = () => {
  const theme = useMantineTheme();
  const navigate = useNavigate();
  const permissions = usePermissions();
  const [selectedJobs, setSelectedJobs] = React.useState<string[]>([]);
  const listVariables = useListVariables<JobFilter>({
    name: "dashboard_jobs",
    defaultSort: { field: "updatedAt", order: "DESC" },
    defaultPagination: { page: 1, perPage: 30 },
    defaultFilter: { status: "TODO" },
  });

  const accountSummary = useGetAccountSummaryQuery({
    fetchPolicy: "cache-and-network",
  });
  const jobs = useListJobsQuery({
    fetchPolicy: "cache-and-network",
    variables: {
      pagination: listVariables.pagination,
      sort: listVariables.sort,
      filter: listVariables.filter,
    },
  });

  return (
    <>
      <Group justify="space-between" mb="sm">
        <StyledPageTitle mb={0}>Dashboard</StyledPageTitle>
        <Group gap="sm">
          <ClockInOutButton />
          <Button
            onClick={() => {
              track("dashboard_action_clicked", { action: "create_job" });
              navigate("/app/jobs/create");
            }}
            rightSection={<IconClipboardList />}
            display={!permissions.can("createJob") ? "none" : undefined}
            color="tsgGreen"
            radius="md"
            variant="filled"
          >
            Create Job
          </Button>
          <Button
            onClick={() => {
              track("dashboard_action_clicked", { action: "create_customer" });
              navigate("/app/customers/create");
            }}
            rightSection={<IconUsersGroup />}
            display={!permissions.can("createCustomer") ? "none" : undefined}
            color="tsgBlue"
            radius="md"
            variant="filled"
          >
            Create Customer
          </Button>
        </Group>
      </Group>
      <Grid>
        <Grid.Col span={{ md: 3, sm: 12 }}>
          <Paper
            withBorder
            shadow="md"
            radius="md"
            h="100%"
            pos="relative"
            p="md"
          >
            <Center h="100%">
              <div>
                <Text
                  size="96px"
                  fw={900}
                  ta="center"
                  style={{
                    color: theme.colors.tsgBlue[7],
                    lineHeight: 1,
                  }}
                >
                  {accountSummary.data?.accountSummary.unfinishedJobs !==
                  undefined
                    ? accountSummary.data?.accountSummary.unfinishedJobs
                    : ""}
                </Text>
                <Text ta="center" size="md" fw={700} mb="md" c="tsgBlue.6">
                  Pending Jobs
                </Text>
              </div>
            </Center>
            <LoadingOverlay
              visible={accountSummary.loading}
              zIndex={1000}
              overlayProps={{ radius: "md", blur: 2 }}
            />
          </Paper>
        </Grid.Col>
        <Grid.Col span={{ md: 9, sm: 12 }}>
          <Paper withBorder shadow="md" radius="md" pos="relative" p="md">
            <StyledSectionTitle order={3} mb="md">
              Jobs Finished by Month
            </StyledSectionTitle>
            <BarChart
              data={(accountSummary.data?.accountSummary.jobsByMonth || []).map(
                (job) => ({
                  date: moment(job.date).format("MMM YYYY"),
                  Jobs: job.jobs,
                })
              )}
              dataKey="date"
              valueFormatter={(value) =>
                new Intl.NumberFormat("en-US").format(value)
              }
              series={[{ name: "Jobs", color: theme.colors.tsgBlue[6] }]}
              h={250}
              tickLine="y"
              withLegend
              legendProps={{
                verticalAlign: "top",
                height: 30,
              }}
            />
            <LoadingOverlay
              visible={accountSummary.loading}
              zIndex={1000}
              overlayProps={{ radius: "md", blur: 2 }}
            />
          </Paper>
        </Grid.Col>
        <Grid.Col span={12} mt="md">
          <Paper withBorder shadow="md" radius="md" p="md" mih={rem(300)}>
            <Group mb="xl" justify="space-between">
              <StyledSectionTitle order={3}>Unfinished Jobs</StyledSectionTitle>

              <FinishJobsButton
                jobIds={selectedJobs}
                onComplete={() => {
                  jobs.refetch();
                  accountSummary.refetch();
                  setSelectedJobs([]);
                }}
                size="md"
                hidden={selectedJobs.length === 0}
              />
            </Group>
            <Table.ScrollContainer minWidth={500}>
              <Table withTableBorder highlightOnHover>
                <Table.Thead bg={theme.colors.tsgBlue[0]}>
                  <Table.Tr c={theme.colors.tsgBlue[8]}>
                    <Table.Th w={0} style={{ textAlign: "center" }}></Table.Th>
                    <TableHeaderCell
                      label="Job Name"
                      field="name"
                      vars={listVariables}
                    />
                    <Table.Th style={{ color: theme.colors.tsgBlue[8] }}>
                      Customer
                    </Table.Th>
                    <TableHeaderCell
                      label="String"
                      field="stringName"
                      vars={listVariables}
                    />
                    <TableHeaderCell
                      label="Racket"
                      field="racket"
                      vars={listVariables}
                    />
                    <TableHeaderCell
                      label="Tension"
                      field="tension"
                      vars={listVariables}
                    />
                    <TableHeaderCell
                      label="Gauge"
                      field="gauge"
                      vars={listVariables}
                    />
                    <TableHeaderCell
                      label="Received On"
                      field="receivedAt"
                      vars={listVariables}
                    />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {jobs.data?.listJobs.data.map((job) => (
                    <Table.Tr
                      key={job.id}
                      data-testid="job-table-row"
                      bg={
                        selectedJobs.includes(job.id)
                          ? theme.colors.tsgBlue[0]
                          : undefined
                      }
                      style={{
                        cursor: "pointer",
                        transition: "background-color 0.2s",
                      }}
                    >
                      <Table.Td>
                        <Checkbox
                          aria-label="Select Job"
                          checked={selectedJobs.includes(job.id)}
                          onChange={(event) =>
                            setSelectedJobs(
                              event.currentTarget.checked
                                ? [...selectedJobs, job.id]
                                : selectedJobs.filter(
                                    (jobId) => jobId !== job.id
                                  )
                            )
                          }
                        />
                      </Table.Td>
                      <Table.Td>
                        <Anchor
                          onClick={() => {
                            track("dashboard_action_clicked", {
                              action: "open_job",
                            });
                            navigate(`/app/jobs/${job.id}`);
                          }}
                        >
                          {job.name}
                        </Anchor>
                      </Table.Td>
                      <Table.Td>
                        <Anchor
                          onClick={() => {
                            track("dashboard_action_clicked", {
                              action: "open_customer",
                            });
                            navigate(`/app/customers/${job.customerId}`);
                          }}
                        >
                          {job.customer?.firstName} {job.customer?.lastName}
                        </Anchor>
                      </Table.Td>
                      <Table.Td>{job.stringName}</Table.Td>
                      <Table.Td>
                        {job.racket}
                        <RacketTypeBadge racketType={job.racketType} />
                      </Table.Td>
                      <Table.Td>{job.tension}</Table.Td>
                      <Table.Td>{job.gauge}</Table.Td>
                      <Table.Td>
                        <DateDisplay value={job.receivedAt || job.createdAt} />
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
            <Group justify="space-between" mt="xl">
              <Text size="sm" c="dimmed">
                {jobs.data?.listJobs.total
                  ? `Total: ${jobs.data?.listJobs.total} jobs`
                  : ""}
              </Text>
              <Pagination
                hidden={
                  !jobs.data?.listJobs.total ||
                  jobs.data?.listJobs.total < listVariables.pagination.perPage
                }
                value={listVariables.pagination.page}
                onChange={listVariables.setPage}
                total={Math.ceil(
                  (jobs.data?.listJobs.total || 0) /
                    listVariables.pagination.perPage
                )}
                color="tsgBlue"
                radius="md"
              />
            </Group>
          </Paper>
        </Grid.Col>
      </Grid>
    </>
  );
};

export default DashboardPage;
